import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../auth/AuthProvider';
import { MessageLoading } from '../../components/Loading';
import Signout from '../../components/Signout';
import {
  useGenerateSigningUrlMutation,
  useGetApplicationJurisdictionLazyQuery
} from '../../generated/graphql';
import getEnv from '../../utils/getEnv';
import Error from '../Error';

const CLASS_NAME = 'Terms m-auto text-lg container text-center';

const {
  REACT_APP_API_MAX_RETRIES,
  REACT_APP_API_BASE_DELAY,
  REACT_APP_API_MAX_DELAY
} = getEnv();

// Validate URLs before assigning to window.location.href to prevent
// open-redirect / XSS via javascript:/data:/vbscript: schemes (Aikido SAST 159108667).
const isSafeRedirectUrl = (value: unknown): value is string => {
  if (typeof value !== 'string' || value.length === 0) {
    return false;
  }
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
};

interface TermsParams {
  quoteId: string;
}

const Terms: React.FC = () => {
  const [reachMaxRetries, setReachMaxRetries] = useState(false);
  const queryParams = new URLSearchParams(location.search);
  // [TODO][Phase-2][Multi-prod] URL param needs to be updated for multi-prod
  let quoteId = queryParams.get('quote') ?? undefined;

  const { quoteId: tempQuoteId } = useParams<Partial<TermsParams>>();

  if (!quoteId) {
    quoteId = tempQuoteId;
  }

  const [
    generateSigningUrl,
    { error: signingUrlError }
  ] = useGenerateSigningUrlMutation();
  const [
    getApplicationJurisdiction,
    { data: applicationData, error: getApplicationError }
  ] = useGetApplicationJurisdictionLazyQuery();
  const {
    user: authUser,
    isLoading: isAuthLoading,
    isAuthenticated: isAuthAuthenticated
  } = useAuth();
  const {
    user: auth0User,
    error: auth0Error,
    isLoading: isAuth0Loading
  } = useAuth0();
  const user = isAuthAuthenticated ? authUser : auth0User;
  const { email_verified, email } = user || {};
  const isEmailValid = email_verified && !!email;

  useEffect(() => {
    if (!isEmailValid) {
      return;
    }

    if (!quoteId) {
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps

    getApplicationJurisdiction({
      variables: { quoteOrGroupId: quoteId }
    });

    // Shared error handler for retry rejections - prevents unhandled promise rejections
    const handleRetryError = () => {
      // Error state is already handled via reachMaxRetries
    };

    const generateSigningUrlWithRetry = async (
      quoteId: string,
      attempt: number,
      maxRetries: number,
      delay: number,
      maxDelay: number
    ) => {
      try {
        const signingUrl = await generateSigningUrl({
          variables: { quoteId }
        });
        const ret = signingUrl.data?.generateSigningURL;
        if (!ret) {
          <Error message="Couldn't get the signing Url" />;
        }
        if (!isSafeRedirectUrl(ret)) {
          setReachMaxRetries(true);
          return;
        }
        window.location.href = ret;
      } catch (error) {
        attempt++;

        if (attempt >= maxRetries) {
          setReachMaxRetries(true);
          throw error;
        }

        // Calculate the next retry delay with exponential backoff and jitter
        const jitter = Math.random() * delay;
        const exponentialBackoff = Math.min(delay * 2, maxDelay);
        delay = Math.min(exponentialBackoff + jitter, maxDelay);

        // Wait for the calculated delay before retrying
        await new Promise((resolve) => setTimeout(resolve, delay));

        generateSigningUrlWithRetry(
          quoteId,
          attempt,
          maxRetries,
          delay,
          maxDelay
        ).catch(handleRetryError);

        return;
      }
    };

    if (applicationData && applicationData.getApplicationJurisdiction) {
      generateSigningUrlWithRetry(
        quoteId,
        0,
        Number(REACT_APP_API_MAX_RETRIES),
        Number(REACT_APP_API_BASE_DELAY),
        Number(REACT_APP_API_MAX_DELAY)
      ).catch(handleRetryError);
    }
  }, [
    isEmailValid,
    quoteId,
    applicationData,
    generateSigningUrl,
    getApplicationJurisdiction
  ]);

  if (signingUrlError && reachMaxRetries) {
    return <Error apolloError={signingUrlError} />;
  }

  if (getApplicationError) {
    return (
      <div className={CLASS_NAME}>
        There is a backend error: {getApplicationError.message}
      </div>
    );
  }

  if (auth0Error) {
    return (
      <div className={CLASS_NAME}>
        There is an Auth0 error: {auth0Error.message}
      </div>
    );
  }

  if (!isAuth0Loading && !isAuthLoading) {
    if (!user) {
      return (
        <div className={CLASS_NAME}>
          Cannot retrieve user profile from Auth0.
        </div>
      );
    }

    if (!isEmailValid) {
      return (
        <div className={`${CLASS_NAME} leading-loose`}>
          <div className="text-lg font-bold">Please keep this tab open.</div>
          We have sent the verification email. Please check your inbox and
          refresh this tab after completing email verification.
        </div>
      );
    }
  }

  return (
    <>
      <Signout />
      <MessageLoading
        headingMessage="Your patience is appreciated!"
        subHeadingMessage="We are creating the documents you'll need to review & sign"
      />
    </>
  );
};

export default Terms;
